import React, { useState } from 'react';
import { FiAlertTriangle } from 'react-icons/fi';

const profileReportReasons = [
    { id: 'fake_profile', label: 'Fake Profile or Spam', description: 'Impersonation, scams, or commercial spam' },
    { id: 'child_safety', label: 'Child Safety Concern', description: 'Underage user or child safety issue (Mandatory)' },
    { id: 'harassment', label: 'Harassment or Bullying', description: 'Threats, extortion, or humiliation' },
    { id: 'inappropriate_content', label: 'Inappropriate Content', description: 'Sexually explicit or pornographic content' },
    { id: 'hate_speech', label: 'Hate Speech', description: 'Promoting violence or hatred based on identity' },
    { id: 'violence', label: 'Violence or Threats', description: 'Threats of physical harm or dangerous activities' },
    { id: 'other', label: 'Something Else', description: 'Issue not listed above' }
];

const postReportReasons = [
    { id: 'child_safety', label: 'Child Safety / Underage Individual', description: 'Post appearing to involve minors (Mandatory)' },
    { id: 'inappropriate_content', label: 'Inappropriate / Nudity', description: 'Sexually explicit or nudity' },
    { id: 'harassment', label: 'Harassment or Bullying', description: 'Shaming, threats, or harassment' },
    { id: 'hate_speech', label: 'Hate Speech or Violence', description: 'Violence or hatred against protected groups' },
    { id: 'dangerous', label: 'Dangerous Activities', description: 'Self-harm, suicide, or illegal acts' },
    { id: 'scam', label: 'Scams, Fraud, or Spam', description: 'Defrauding users or commercial spam' },
    { id: 'ip_violation', label: 'Intellectual Property Violation', description: 'Copyright or trademark infringement' },
    { id: 'other', label: 'Other Concern', description: 'Issue not listed above' }
];

const ReportModal = ({ isOpen, onClose, userName, onSubmit, isSubmitting, type = 'profile' }) => {
    const [selectedReason, setSelectedReason] = useState('');
    const [description, setDescription] = useState('');
    const [error, setError] = useState('');

    // Select reasons based on type
    const reasons = type === 'post' ? postReportReasons : profileReportReasons;

    if (!isOpen) return null;

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!selectedReason) {
            setError('Please select a reason for reporting');
            return;
        }
        if (!description.trim()) {
            setError('Please provide a description');
            return;
        }

        onSubmit({
            reason: selectedReason,
            description: description
        });
    };

    return (
        <div className="fixed inset-0 z-[100] overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                {/* Background overlay */}
                <div
                    className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                    aria-hidden="true"
                    onClick={onClose}
                ></div>

                <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

                <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg w-full">
                    <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                        <div className="sm:flex sm:items-start">
                            <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                                <FiAlertTriangle className="h-6 w-6 text-red-600" />
                            </div>
                            <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                                <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                                    Report {type === 'post' ? 'Post' : userName}
                                </h3>
                                <div className="mt-2">
                                    <p className="text-sm text-gray-500 mb-4">
                                        Please select the reason for reporting this content. Reports are anonymous and handled urgently.
                                    </p>

                                    <form id="report-form" onSubmit={handleSubmit}>
                                        <div className="space-y-3 max-h-60 overflow-y-auto mb-4">
                                            {reasons.map((reason) => (
                                                <div
                                                    key={reason.id}
                                                    className={`relative flex items-start p-3 rounded-lg border cursor-pointer hover:bg-gray-50 transition-colors ${selectedReason === reason.id ? 'border-red-500 bg-red-50 hover:bg-red-50' : 'border-gray-200'
                                                        }`}
                                                    onClick={() => setSelectedReason(reason.id)}
                                                >
                                                    <div className="flex items-center h-5">
                                                        <input
                                                            type="radio"
                                                            name="report-reason"
                                                            value={reason.id}
                                                            checked={selectedReason === reason.id}
                                                            onChange={() => setSelectedReason(reason.id)}
                                                            className="focus:ring-red-500 h-4 w-4 text-red-600 border-gray-300"
                                                        />
                                                    </div>
                                                    <div className="ml-3 text-sm">
                                                        <label className={`font-medium ${selectedReason === reason.id ? 'text-red-700' : 'text-gray-700'}`}>
                                                            {reason.label}
                                                        </label>
                                                        <p className="text-gray-500 text-xs mt-0.5">{reason.description}</p>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>

                                        <div className="mt-4">
                                            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                                                Additional Details {selectedReason === 'other' && '*'}
                                            </label>
                                            <textarea
                                                id="description"
                                                rows="3"
                                                className="shadow-sm focus:ring-red-500 focus:border-red-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border"
                                                placeholder="Please provide specific details about your concern..."
                                                value={description}
                                                onChange={(e) => setDescription(e.target.value)}
                                                required
                                            ></textarea>
                                        </div>

                                        {error && (
                                            <div className="mt-2 text-sm text-red-600">
                                                {error}
                                            </div>
                                        )}
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                        <button
                            type="submit"
                            form="report-form"
                            disabled={isSubmitting}
                            className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                            {isSubmitting ? (
                                <span className="flex items-center">
                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Submitting...
                                </span>
                            ) : (
                                'Submit Report'
                            )}
                        </button>
                        <button
                            type="button"
                            className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                            onClick={onClose}
                            disabled={isSubmitting}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ReportModal;
